<template>
  <div class="boolean-search">
    <last-activity-modal v-if="activeLastActivity" />
    <router-view :key="pageIndex" />
  </div>
</template>

<script>

// Config
import { API_HOST } from '../config/app-const';

// Plugins
import {mapActions, mapState} from 'vuex'
import axios from 'axios'
import debounce from 'lodash/debounce'

// Mixins
import notifyMessages from "../mixins/boolean-search/notifyMessages";
import storeHelper from '../mixins/boolean-search/storeHelper';

// Components
import lastActivityModal from '../components/boolean-search/modals/LastActivityModal.vue'


export default {
  name: "boolean-search",

  mixins: [ notifyMessages, storeHelper ],

  props: {
    client_id: String,
    project_id: String,
  },

  beforeCreate() {
    const mains = document.getElementsByClassName('main')
    Array.from(mains).forEach(main => main.style.overflow = 'visible')

  },

  created() {
    // cache project_id on store
    this.$store.commit('booleanSearch/SET_CLIENT_ID', this.client_id)
    this.$store.commit('booleanSearch/SET_PROJECT_ID', this.project_id)

    // set baseUrl && Auth token for api calls
    axios.defaults.baseURL = API_HOST;
    axios.defaults.headers.common['Authorization'] = `Token token=${this.user.accessToken}`;

    this.getPaginatedResource({key: 'searches'}).catch(() => this.notifyStoreError())
    this.getPaginatedResource({key:  'group_library', url: `projects/${this.project_id}/searches/group_library`}).catch(() => this.notifyStoreError())
    this.getPaginatedResource({key: 'tag_library', url: `projects/${this.project_id}/searches/tag_library`}).catch(() => this.notifyStoreError())

    // load languages
    this.getLanguages({projectId: this.project_id})
    this.getCountries({projectId: this.project_id, pagination: { page:1, per_page: 300, order: 'name', sort: 'asc' }})
        .then(({data: {data}}) => {
          const usa = data.find(item => item.name === "United States")
          this.getStates(usa.alpha_2_code)
        })
    this.getResources({key: 'outlet_categories', url: `projects/${this.project_id}/searches/search_industries`})
    this.getTagsCategories({key: 'tagCategories', url: `projects/${this.project_id}/searches/tag_library/tagCategories`})
    this.getOutletGroups({projectId: this.project_id}).then(({data : { data: resourceData, pagination}}) => {
      this.$store.commit('booleanSearch/SET_PAGINATED_RESOURCE', {key: 'include_outlet_groups', resourceData, pagination })
      this.$store.commit('booleanSearch/SET_PAGINATED_RESOURCE', {key: 'exclude_outlet_groups', resourceData, pagination })
    })
    this.getOutlets({key: 'outlets', url: 'outlets', extraFilters: { order: 'label', sort: 'asc' }}).then(({data : { data: resourceData, pagination}}) => {
      this.$store.commit('booleanSearch/SET_PAGINATED_RESOURCE', {key: 'include_outlets', resourceData, pagination })
      this.$store.commit('booleanSearch/SET_PAGINATED_RESOURCE', {key: 'exclude_outlets', resourceData, pagination })
    })
    // The endpoint this function is calling doesn't seem to exist
    // this.getTags({key: 'tags'}) // wouldn't bet the "getTagsByProject" better? it's not paginated, which would be great

    // projects and clients for cloning
    this.getProjects({key: 'projects', url: `projects/${this.project_id}/search`})
    this.getClients({key: 'clients', url: `clients`})

    this.getResources({key: 'twitter_languages', url: `twitter/languages`})
    this.getResources({key: 'twitter_filters', url: `twitter/filters`})
    this.getResources({key: 'search_terms_filters', url: `twitter/x_netfeedr_filter`})
    this.getResources({key: 'x_channel_filters', url: `twitter/x_netfeedr_filter?channel=true`})

    // Get history
    this.getLastActivities();

    // attach Resize event listener
    window.addEventListener('resize', this.handleResize);
  },

  destroyed () {
    window.removeEventListener('resize', this.handleResize);
  },

  data() {
    return {
      pageIndex: 1
    }
  },

  computed: {
    ...mapState({
      user: state => state.user.user.data,
      activeLastActivity: state => state.booleanSearch.active_last_activity,
      reloadLastActivity: state => state.booleanSearch.reload_last_activity,
      clonedSearch: state => state.booleanSearch.clonedSearch
    }),
    resourceType () {
      return this.$route.meta && this.$route.meta.isGroupType ? 'group' : this.$route.meta.isTagSearchType ? 'tagSearch' : 'search';
    },
    resourceId () {
      return this.$route.params.group_id || this.$route.params.search_id || this.$route.params.tagSearch_id;
    }
  },

  methods: {
    ...mapActions({
      getPaginatedResource: "booleanSearch/getPaginatedResource",
      getResources        : "booleanSearch/getResources",
      getLanguages        : "languages/getLanguages",
      getCountries        : "countries/getCountries",
      getOutletGroups     : "outletGroups/getOutletGroups",
      getOutlets          : "booleanSearch/searchGenericPaginatedResource",
      getTags             : "booleanSearch/getPaginatedResource",
      getTagsCategories   : "booleanSearch/getTagsCategories",
      getTwitterFilters   : "booleanSearch/searchGenericPaginatedResource",
      getProjects         : "booleanSearch/getPaginatedResource",
      getClients          : "booleanSearch/getPaginatedResource",
      getStates           : "booleanSearch/getStates",
    }),
    handleResize: debounce(function () {
      this.$store.commit('booleanSearch/SET_WINDOW_IS_RESIZING', true)
      this.$nextTick(() => this.$store.commit('booleanSearch/SET_WINDOW_IS_RESIZING', false))
    }, 100),

    getLastActivities () {
      this.resetPaginatedResource('last_activities')
      const queryId = this.resourceId ? `/${this.resourceId}` : '';
      const groupType = this.resourceType === 'group' ? `/group_library` : this.resourceType === 'tagSearch' ? `/tag_library` : '';

      this.getPaginatedResource({
        key: 'last_activities',
        url: `projects/${this.project_id}/searches${groupType}/history${queryId}`
      });
    }
  },

  watch: {
    $route: {
      handler(newValue, oldValue) {
        this.pageIndex++;
        if(this.clonedSearch) this.$store.commit('booleanSearch/SET_CLONED_SEARCH', null);

        if((newValue.meta.isGroupType !== oldValue.meta.isGroupType) ||
          (newValue.params.group_id !== oldValue.params.group_id) ||
          (newValue.meta.isTagSearchType !== oldValue.meta.isTagSearchType) ||
          (newValue.params.tagSearch_id !== oldValue.params.tagSearch_id) ||
          (newValue.params.search_id !== oldValue.params.search_id)) {
          this.getLastActivities();
        }
      }
    },
    reloadLastActivity: {
      handler (newValue, oldValue) {
        if(newValue && oldValue !== newValue) {
          this.getLastActivities();
        }
      }
    }
  },

  components: {
    lastActivityModal
  }
}
</script>

<style lang="scss">
@import "../styles/components/boolean-search/index.scss";
</style>